/**
 * Input Sanitization and Validation Utilities
 * Provides client-side protection against XSS and SQL injection attacks
 */

/**
 * Escapes HTML special characters to prevent XSS attacks
 * @param {string} str - The string to escape
 * @returns {string} The escaped string
 */
export const escapeHtml = (str) => {
  if (typeof str !== 'string') return str;
  
  const htmlEscapeMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  
  return str.replace(/[&<>"'/]/g, (char) => htmlEscapeMap[char]);
};

/**
 * Removes potentially dangerous characters from input
 * @param {string} input - The input to sanitize
 * @returns {string} Sanitized input
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove null bytes
  let sanitized = input.replace(/\0/g, '');
  
  // Remove script tags and event handlers
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  
  return sanitized;
};

/**
 * Validates that input doesn't contain SQL injection patterns
 * Note: This is client-side validation only. Backend MUST use parameterized queries.
 * @param {string} input - The input to validate
 * @returns {boolean} True if input appears safe
 */
export const isSafeSqlInput = (input) => {
  if (typeof input !== 'string' || !input.trim()) return true;
  
  // Pattern to detect common SQL injection attempts
  const sqlInjectionPattern = /(\b(SELECT|DROP|INSERT|UPDATE|DELETE|UNION|EXEC|EXECUTE|CREATE|ALTER|TRUNCATE)\b|--|;|\/\*|\*\/|'|"|\bOR\b.*=|\bAND\b.*=)/gi;
  
  return !sqlInjectionPattern.test(input);
};

/**
 * Validates that input doesn't contain XSS patterns
 * @param {string} input - The input to validate
 * @returns {boolean} True if input appears safe
 */
export const isSafeFromXSS = (input) => {
  if (typeof input !== 'string' || !input.trim()) return true;
  
  const xssPattern = /<script|javascript:|onerror=|onload=|onclick=|<iframe|<embed|<object/gi;
  
  return !xssPattern.test(input);
};

/**
 * Comprehensive input validation
 * @param {string} input - The input to validate
 * @returns {object} { isValid: boolean, errors: string[] }
 */
export const validateInput = (input) => {
  const errors = [];
  
  if (!isSafeSqlInput(input)) {
    errors.push('Input contains potentially dangerous SQL patterns');
  }
  
  if (!isSafeFromXSS(input)) {
    errors.push('Input contains potentially dangerous script patterns');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates free-text comment input.
 *
 * Comment fields are meant to accept normal prose, including punctuation such
 * as quotes, commas, semicolons, and apostrophes. We therefore only block
 * clearly dangerous script patterns here.
 *
 * @param {string} input - The comment to validate
 * @returns {object} { isValid: boolean, errors: string[] }
 */
export const validateCommentInput = (input) => {
  const errors = [];

  if (!isSafeFromXSS(input)) {
    errors.push('Input contains potentially dangerous script patterns');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Sanitizes email addresses
 * @param {string} email - The email to sanitize
 * @returns {string} Sanitized email
 */
export const sanitizeEmail = (email) => {
  if (typeof email !== 'string') return email;
  
  // Remove whitespace and convert to lowercase
  let sanitized = email.trim().toLowerCase();
  
  // Remove any HTML tags
  sanitized = sanitized.replace(/<[^>]*>/g, '');
  
  return sanitized;
};

/**
 * Validates email format
 * @param {string} email - The email to validate
 * @returns {boolean} True if email format is valid
 */
export const isValidEmail = (email) => {
  if (typeof email !== 'string') return false;
  
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(email);
};

/**
 * Sanitizes numeric input
 * @param {string} input - The numeric input
 * @returns {string} Sanitized numeric string
 */
export const sanitizeNumeric = (input) => {
  if (typeof input !== 'string') return input;
  
  // Keep only digits, decimal point, and minus sign
  return input.replace(/[^0-9.-]/g, '');
};

/**
 * Sanitizes alphanumeric input (usernames, codes, etc.)
 * @param {string} input - The input to sanitize
 * @returns {string} Sanitized alphanumeric string
 */
export const sanitizeAlphanumeric = (input) => {
  if (typeof input !== 'string') return input;
  
  // Keep only alphanumeric, underscore, and hyphen
  return input.replace(/[^a-zA-Z0-9_-]/g, '');
};

/**
 * Validates username format
 * @param {string} username - The username to validate
 * @returns {object} { isValid: boolean, error: string }
 */
export const validateUsername = (username) => {
  if (!username || typeof username !== 'string') {
    return { isValid: false, error: 'Username is required' };
  }
  
  if (username.length < 3) {
    return { isValid: false, error: 'Username must be at least 3 characters' };
  }
  
  if (username.length > 50) {
    return { isValid: false, error: 'Username must not exceed 50 characters' };
  }
  
  if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
    return { isValid: false, error: 'Username can only contain letters, numbers, underscores, and hyphens' };
  }
  
  if (/\s/.test(username)) {
    return { isValid: false, error: 'Username cannot contain spaces' };
  }
  
  return { isValid: true };
};

/**
 * Validates password strength
 * @param {string} password - The password to validate
 * @returns {object} { isValid: boolean, errors: string[], strength: string }
 */
export const validatePassword = (password) => {
  const errors = [];
  let strength = 'weak';
  
  if (!password || typeof password !== 'string') {
    return { isValid: false, errors: ['Password is required'], strength: 'none' };
  }
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  
  if (!/(?=.*[a-z])/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/(?=.*[A-Z])/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/(?=.*\d)/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  // Calculate strength
  if (errors.length === 0) {
    if (password.length >= 12 && /(?=.*[!@#$%^&*])/.test(password)) {
      strength = 'strong';
    } else if (password.length >= 10) {
      strength = 'medium';
    } else {
      strength = 'fair';
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    strength,
  };
};

/**
 * Sanitizes filename to prevent path traversal attacks
 * @param {string} filename - The filename to sanitize
 * @returns {string} Sanitized filename
 */
export const sanitizeFilename = (filename) => {
  if (typeof filename !== 'string') return filename;
  
  // Remove path separators and parent directory references
  let sanitized = filename.replace(/[/\\]/g, '');
  sanitized = sanitized.replace(/\.\./g, '');
  sanitized = sanitized.replace(/\0/g, '');
  
  // Remove special characters
  sanitized = sanitized.replace(/[^a-zA-Z0-9._-]/g, '_');
  
  return sanitized;
};

/**
 * Validates filename safety
 * @param {string} filename - The filename to validate
 * @returns {boolean} True if filename is safe
 */
export const isSafeFilename = (filename) => {
  if (!filename || typeof filename !== 'string') return false;
  
  // Check for path traversal attempts
  if (filename.includes('..') || filename.includes('/') || filename.includes('\\')) {
    return false;
  }
  
  // Check for null bytes
  if (filename.includes('\0')) {
    return false;
  }
  
  return true;
};

/**
 * Creates a safe form data object by sanitizing all string values
 * @param {object} formData - The form data to sanitize
 * @returns {object} Sanitized form data
 */
export const sanitizeFormData = (formData) => {
  if (!formData || typeof formData !== 'object') return formData;
  
  const sanitized = {};
  
  for (const [key, value] of Object.entries(formData)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeInput(value);
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map(item => 
        typeof item === 'string' ? sanitizeInput(item) : item
      );
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

/**
 * Validates all fields in a form data object
 * @param {object} formData - The form data to validate
 * @param {object} rules - Validation rules for each field
 * @returns {object} { isValid: boolean, errors: object }
 */
export const validateFormData = (formData, rules = {}) => {
  const errors = {};
  
  for (const [field, value] of Object.entries(formData)) {
    const rule = rules[field];
    
    if (rule) {
      // Required validation
      if (rule.required && (!value || (typeof value === 'string' && !value.trim()))) {
        errors[field] = rule.requiredMessage || `${field} is required`;
        continue;
      }
      
      // String validations
      if (typeof value === 'string' && value) {
        if (rule.minLength && value.length < rule.minLength) {
          errors[field] = rule.minLengthMessage || `${field} must be at least ${rule.minLength} characters`;
        }
        
        if (rule.maxLength && value.length > rule.maxLength) {
          errors[field] = rule.maxLengthMessage || `${field} must not exceed ${rule.maxLength} characters`;
        }
        
        if (rule.pattern && !rule.pattern.test(value)) {
          errors[field] = rule.patternMessage || `${field} format is invalid`;
        }
        
        // Security validations
        if (rule.checkXSS && !isSafeFromXSS(value)) {
          errors[field] = 'This field contains potentially dangerous content';
        }
        
        if (rule.checkSQL && !isSafeSqlInput(value)) {
          errors[field] = 'This field contains potentially dangerous patterns';
        }
      }
      
      // Custom validator
      if (rule.validator && typeof rule.validator === 'function') {
        const customError = rule.validator(value, formData);
        if (customError) {
          errors[field] = customError;
        }
      }
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

const inputValidation = {
  escapeHtml,
  sanitizeInput,
  isSafeSqlInput,
  isSafeFromXSS,
  validateInput,
  sanitizeEmail,
  isValidEmail,
  sanitizeNumeric,
  sanitizeAlphanumeric,
  validateUsername,
  validatePassword,
  sanitizeFilename,
  isSafeFilename,
  sanitizeFormData,
  validateFormData,
};

export default inputValidation;
